/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { GuidedExpressionEditor } from "abreact/expression/ui/editor/GuidedExpressionEditor";
import { AbstractRowBasedModelImpl } from "abreact/expression/ui/editor/impl/model/impl/AbstractRowBasedModelImpl";
import { OperatorOperandExpressionNodeConverter } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/converter/OperatorOperandExpressionNodeConverter";
import { OperatorOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/OperatorOperandValueImpl";
import { ConditionRowLanguageSource } from "abreact/incubator/ui/abBooleanLogicTree/languageSource/ConditonRowLanguageSource";
import { AbBooleanLogicGroupingContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicGroupingContent";
import { ExpExpressionNodeNodeTypeEnum } from "abweb/generated/abwebinternal/api/dto/ExpExpressionNode";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
// import AbstractModel = GuidedExpressionEditor.AbstractModel;
// import AbstractOperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractOperandValue;
// import OperatorOperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue;
// import RowModel = GuidedExpressionEditor.AbstractRowBasedModel.RowModel;
// import BooleanModel = GuidedExpressionEditor.BooleanModel;

export class BooleanModelImpl extends AbstractRowBasedModelImpl {
  constructor(specification) {
    if (specification.expExpressionDefinition != null) {
      Assert.assert(Expression.Definition.isBooleanDefinition(specification.expExpressionDefinition));
    }
    super(specification, Expression.Domain.Constants.ROLE_CONDITION);
  }
  get supportsSubgroups() {
    return true;
  }
  get rowLanguageSource() {
    return new ConditionRowLanguageSource();
  }
  applyGroupingContent(groupingContent, joinOperandValuesByGroupingContentKey, expressionDomainModel) {
    const expOperator = expressionDomainModel.getExpOperator(groupingContent.connective);
    const partialState = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue.partialStateFromExpOperator(expOperator, expressionDomainModel);
    let joinOperandValue = this.findJoinOperandValue(groupingContent.key);
    if (joinOperandValue == null) {
      joinOperandValue = new OperatorOperandValueImpl({
        id: this.generateId(),
        ...partialState
      });
    } else {
      joinOperandValue = OperatorOperandValueImpl.cast(joinOperandValue).with(partialState);
    }
    joinOperandValuesByGroupingContentKey.set(groupingContent.key, joinOperandValue);
  }

  /**
   * If the declared root operator operand value lacks {@link ROLE_JOIN}, then this has been authored as a single
   * condition expression. In this case, wrap it in a default join operator operand value to create the structure
   * required by {@link GuidedRowBasedExpressionEditor}.
   */
  buildRootJoinOperandValue(expExpressionNode, specification) {
    const converter = new OperatorOperandExpressionNodeConverter(specification);
    let rootOperatorOperandValue = converter.convert(expExpressionNode);
    if (!rootOperatorOperandValue.isJoin) {
      const conditionOperatorOperandValue = rootOperatorOperandValue;
      rootOperatorOperandValue = this.createRootJoinOperandValue(specification.expressionDomainModel);
      rootOperatorOperandValue = rootOperatorOperandValue.withArgumentOperandValue(conditionOperatorOperandValue);
    }
    return rootOperatorOperandValue;
  }
  createRootJoinOperandValue(expressionDomainModel) {
    const defaultJoinExpOperator = expressionDomainModel.getExpOperator(this.defaultConnective);
    return new OperatorOperandValueImpl({
      id: this.generateId(),
      ...GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue.partialStateFromExpOperator(defaultJoinExpOperator, expressionDomainModel)
    });
  }
  buildRootGroupingContent(rootJoinOperandValue, expressionDomainModel, rowModelsByRowKey, joinOperandValuesByGroupingContentKey) {
    const rootGroupingContent = this.createGroupingContentForJoinOperatorOperandValue(rootJoinOperandValue, expressionDomainModel);
    return this.walkJoinOperatorOperandValueRecursive(rootJoinOperandValue, rootGroupingContent, expressionDomainModel, rowModelsByRowKey, joinOperandValuesByGroupingContentKey);
  }
  walkJoinOperatorOperandValueRecursive(parentOperatorOperandValue, parentGroupingContent, expressionDomainModel, rowModelsByRowKey, joinOperandValuesByGroupingContentKey) {
    this.assertIsJoinOperatorOperandValue(parentOperatorOperandValue);
    for (const argumentOperandValue of parentOperatorOperandValue.argumentOperandValuesByOperandName.valueSeq().toArray()) {
      const operatorOperandValue = OperatorOperandValueImpl.cast(argumentOperandValue);
      if (Expression.Domain.OperatorRole.isJoin(operatorOperandValue.expOperator)) {
        let groupingContent = this.createGroupingContentForJoinOperatorOperandValue(operatorOperandValue, expressionDomainModel);
        groupingContent = this.walkJoinOperatorOperandValueRecursive(operatorOperandValue, groupingContent, expressionDomainModel, rowModelsByRowKey, joinOperandValuesByGroupingContentKey);
        joinOperandValuesByGroupingContentKey.set(groupingContent.key, operatorOperandValue);
        parentGroupingContent = parentGroupingContent.withAppendSubgroup(groupingContent);
      } else {
        const conditionContent = this.createConditionContent(operatorOperandValue, rowModelsByRowKey);
        parentGroupingContent = parentGroupingContent.withAppendSingleRow(conditionContent);
      }
    }
    return parentGroupingContent;
  }
  createGroupingContentForJoinOperatorOperandValue(operatorOperandValue, expressionDomainModel) {
    this.assertIsJoinOperatorOperandValue(operatorOperandValue);
    const connective = expressionDomainModel.getConnective(operatorOperandValue.expOperator);
    let groupingContent = new AbBooleanLogicGroupingContent(operatorOperandValue.id, connective);
    return groupingContent.withDisabled(operatorOperandValue.isDisabledOrDefault);
  }
  assertIsJoinOperatorOperandValue(operatorOperandValue) {
    Assert.assert(Expression.Domain.OperatorRole.isJoin(operatorOperandValue.expOperator), `Operator roleID ${operatorOperandValue.expOperator.roleID} not valid for an ${ExpExpressionNodeNodeTypeEnum.OPERATOR} on node with id: ${operatorOperandValue.id}`);
  }
}
(function (_BooleanModelImpl) {
  function isInstance(model) {
    return ClassUtils.isInstanceOf(model, BooleanModelImpl);
  }
  _BooleanModelImpl.isInstance = isInstance;
})(BooleanModelImpl || (BooleanModelImpl = {}));
//# sourceMappingURL=BooleanModelImpl.js.map
